/**
 * Input component - Mobile-first con touch target adecuado
 * Mínimo 48px de altura para cumplir con accesibilidad táctil
 */

import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes } from 'react';
import { cn, QUARTER_HOUR_STEP_SECONDS } from '../../../utils';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
    label: string;
    error?: string;
    autoSelectOnFocus?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
    ({ label, error, className = '', id, autoSelectOnFocus = true, onFocus, type, step, ...props }, ref) => {
        const generatedId = useId();
        const inputId = id || generatedId;
        const errorId = `${inputId}-error`;
        const normalizedType = type ?? 'text';
        const defaultStep = normalizedType === 'time' || normalizedType === 'datetime-local'
            ? QUARTER_HOUR_STEP_SECONDS
            : undefined;
        const shouldSkipSelection = [
            'search',
            'date',
            'time',
            'datetime-local',
            'month',
            'week',
            'color',
            'file',
            'checkbox',
            'radio',
            'range',
            'hidden',
        ].includes(normalizedType);

        const handleFocus: InputHTMLAttributes<HTMLInputElement>['onFocus'] = (event) => {
            if (
                autoSelectOnFocus
                && !shouldSkipSelection
                && !event.currentTarget.readOnly
                && event.currentTarget.value !== ''
            ) {
                event.currentTarget.select();
            }

            onFocus?.(event);
        };

        return (
            <div className={cn('app-form-field flex flex-col gap-2', className)}>
                <label
                    htmlFor={inputId}
                    className="text-sm font-medium text-text-secondary"
                >
                    {label}
                </label>
                <input
                    ref={ref}
                    id={inputId}
                    className={cn(
                        'min-h-12 w-full rounded-xl border bg-surface px-4 py-3 text-base text-text transition focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:bg-disabled-bg disabled:text-text-muted',
                        error
                            ? 'border-error focus-visible:ring-error/25'
                            : 'border-border focus-visible:border-primary focus-visible:ring-primary/25'
                    )}
                    aria-invalid={!!error}
                    aria-describedby={error ? errorId : undefined}
                    type={normalizedType}
                    step={step ?? defaultStep}
                    onFocus={handleFocus}
                    {...props}
                />
                {error && (
                    <span id={errorId} className="text-xs text-error" role="alert">
                        {error}
                    </span>
                )}
            </div>
        );
    }
);

Input.displayName = 'Input';
